import {
  Box,
  Button,
  Divider,
  Grid,
  Link,
  List,
  ListItem,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { PageContainer } from '@toolpad/core/PageContainer';
import { useEffect } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import MfaManagementPanel from '../components/Account/MfaManagementPanel';
import SubscriptionStatusMessage from '../components/Account/SubscriptionStatusMessage';
import SharepointOAuthDisconnect from '../components/Auth/SharepointOAuthDisconnect';
import { useAccount } from '../contexts/AccountContext';

interface Subscription {
  level: string;
  status: 'trialing' | 'active' | 'canceled' | 'past_due' | 'unpaid';
  current_period_end?: Date;
  cancel_at_period_end?: boolean;
  limits: {
    numTenders: number;
    numDrafts: number;
    numReviews: number;
    numReports: number;
    numDraftsPerTender: number;
    numReviewsPerTender: number;
    numReportsPerTender: number;
    numAdHocDocs: number;
    numAdHocDocsPerTender: number;
  };
}

const AccountPage = () => {
  const { account, refreshUserDetails } = useAccount();
  const theme = useTheme();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get('sharepoint_connected') === 'true') {
      refreshUserDetails();
    }
  }, [searchParams, refreshUserDetails]);

  const formatTrialDate = (dateInput?: string | Date | { toDate: () => Date }) => {
    if (!dateInput) {
      return 'N/A';
    }
    try {
      let date: Date;
      if (
        typeof dateInput === 'object' &&
        'toDate' in dateInput &&
        typeof dateInput.toDate === 'function'
      ) {
        date = dateInput.toDate();
      } else {
        date = new Date(dateInput as string | Date);
      }
      const userTimezone = account?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
      return new Intl.DateTimeFormat(undefined, {
        dateStyle: 'long',
        timeZone: userTimezone,
      }).format(date);
    } catch (_error) {
      return 'Invalid Date';
    }
  };

  const subscription = account?.subscription as Subscription | undefined;

  const isExpired =
    subscription?.current_period_end &&
    new Date(subscription.current_period_end).getTime() < Date.now();

  const isMultiUser = (account?.clientUserCount || 0) > 1;

  const getEffectiveStatus = () => {
    if (!subscription) {
      return 'none';
    }
    if (isExpired && subscription.status === 'active') {
      return 'expired';
    }
    return subscription.status;
  };
  const effectiveStatus = getEffectiveStatus();

  const planName = 'Pay-As-You-Go';

  return (
    <PageContainer title="My Account">
      <Box sx={{ pb: 4, mt: 0 }}>
        {/* Personal Information Section */}
        <Paper elevation={3} sx={{ padding: theme.spacing(3), mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            Personal Information
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Display Name"
                value={account?.displayName || ''}
                fullWidth
                variant="filled"
                disabled
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Email Address"
                value={account?.email || ''}
                fullWidth
                variant="filled"
                disabled
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Phone Number"
                value={account?.phoneNumber || ''}
                fullWidth
                variant="filled"
                disabled
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="body2" color="text.primary">
                To change your name, email, or phone number, please contact support.
              </Typography>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="body2" color="text.primary">
                View our{' '}
                <RouterLink to="/terms-conditions" style={{ color: theme.palette.bidlogiq.brick }}>
                  Terms and Conditions
                </RouterLink>
                .
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        <Grid container spacing={4} sx={{ alignItems: 'stretch' }}>
          {/* Subscription & Billing Section */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={3} sx={{ padding: theme.spacing(3), height: '100%' }}>
              <Typography variant="h5" gutterBottom>
                Subscription & Billing
              </Typography>
              {subscription ? (
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid
                    size={{
                      xs: 12,
                      md: account?.billingType === 'self-managed' ? 6 : 12,
                    }}
                  >
                    <Typography variant="body1">
                      <strong>Current Plan:</strong>{' '}
                      {effectiveStatus === 'expired' ? (
                        <Typography
                          component="span"
                          sx={{
                            color: theme.palette.text.error,
                            fontWeight: 'bold',
                          }}
                        >
                          Expired ({planName})
                        </Typography>
                      ) : (
                        planName
                      )}
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <SubscriptionStatusMessage
                        effectiveStatus={effectiveStatus}
                        subscription={subscription}
                        formatTrialDate={formatTrialDate}
                      />
                    </Box>
                  </Grid>
                  {/* {account?.billingType === 'self-managed' && (
                    <Grid
                      size={{ xs: 12, md: 6 }}
                      sx={{ textAlign: { xs: 'left', md: 'right' }, mt: { xs: 2, md: 0 } }}
                    >
                      <Button component={RouterLink} to="/billing" variant="contained">
                        {effectiveStatus === 'expired' ? 'Renew Plan' : 'Add/Change Plans'}
                      </Button>
                      {effectiveStatus === 'active' && (
                        <Button
                          variant="text"
                          sx={{
                            mt: 1,
                            display: 'block',
                            ml: 'auto',
                            color: theme.palette.bidlogiq?.brick || theme.palette.error.main,
                          }}
                          onClick={handleManageSubscription}
                        >
                          Cancel Subscription
                        </Button>
                      )}
                    </Grid>
                  )} */}
                </Grid>
              ) : (
                <Typography color="text.primary">
                  Subscription details are not available.
                </Typography>
              )}
            </Paper>
          </Grid>

          {/* Security Section */}
          <Grid size={{ xs: 12, md: 6 }}>
            <MfaManagementPanel />
          </Grid>
        </Grid>

        {/* Storage Configuration Section */}
        {!isMultiUser && (
          <Paper elevation={3} sx={{ padding: theme.spacing(3), mt: 4 }}>
            <Typography variant="h5" gutterBottom>
              Storage Configuration
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Your tender documents are currently stored in:{' '}
              <strong>
                {account?.storageType === 'sharepoint'
                  ? 'Microsoft SharePoint'
                  : 'BIDLOGIQ Cloud Storage (Australia - Sydney)'}
              </strong>
            </Typography>
            <Box sx={{ mb: 3 }}>
              {account?.storageType === 'sharepoint' ? (
                <>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      mb: 2,
                      border: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Typography variant="subtitle1" gutterBottom>
                      Important Note
                    </Typography>
                    <Typography variant="body2">
                      To ensure data integrity and proper functionality, please manage all tender
                      documents exclusively through the BIDLOGIQ user interface. Do not add, delete,
                      or modify files directly in the SharePoint site.
                    </Typography>
                  </Paper>
                  <SharepointOAuthDisconnect />
                </>
              ) : (
                <>
                  <Button
                    component={RouterLink}
                    to="/sharepoint-setup"
                    variant="contained"
                    color="primary"
                    sx={{ mb: 1 }}
                  >
                    Connect to SharePoint
                  </Button>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Prerequisites for Connecting
                  </Typography>
                  <List sx={{ textAlign: 'left', maxWidth: 'md' }} dense>
                    <ListItem>
                      <ListItemText
                        primary={<Typography variant="subtitle2">Administrator Role</Typography>}
                        secondary={
                          <Typography variant="caption">
                            Requires Global or Application Administrator role in Microsoft 365.
                          </Typography>
                        }
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary={<Typography variant="subtitle2">Azure Portal Access</Typography>}
                        secondary={
                          <Typography variant="caption" component="span">
                            Requires access to the{' '}
                            <Link href="https://portal.azure.com" target="_blank" rel="noopener">
                              Azure Portal
                            </Link>
                            .
                          </Typography>
                        }
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary={<Typography variant="subtitle2">Connection Steps</Typography>}
                        secondary={
                          <Typography variant="caption">
                            The wizard will guide you through creating a site, registering an app,
                            and granting permissions.
                          </Typography>
                        }
                      />
                    </ListItem>
                  </List>
                </>
              )}
            </Box>
            <Typography>
              <strong>Need a different storage region or platform? Contact us! </strong>
              <br />
              <strong>Email: </strong>{' '}
              <a href="mailto:support@bidlogiq.com" style={{ color: theme.palette.bidlogiq.brick }}>
                support@bidlogiq.com
              </a>
              <br />
              <strong>AUS:</strong>+61 (0)7 2143 6035
              <br />
            </Typography>
          </Paper>
        )}
      </Box>
    </PageContainer>
  );
};

export default AccountPage;
